import React, { Dispatch, SetStateAction } from "react";
import { useSelector } from "react-redux";

import { RootState } from "src/redux/reducers";
import {
  ExtensionTypeEnum,
  SocialProfileFlags,
  WsApiConnection,
} from "src/types";
import { AppConfig } from "src/config/AppConfig";
import { copyLink } from "src/utils/methods";
import { ReactComponent as LinkIcon } from "src/images/link.svg";
import { ReactComponent as CreatorIcon } from "src/images/avatar.svg";
import { ReactComponent as TickIcon } from "src/images/tick-star.svg";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { Row } from "src/redux/dictionary/types";
import { DropdownMenuHoverItem } from "src/app/components/Dropdown/components/DropdownMenuHoverItem";
import { DropdownMenuItem } from "src/app/components/Dropdown/Dropdown";

interface CopyLinkMenuItemProps {
  setIsDropdownOpen: Dispatch<SetStateAction<boolean>>;
  rowData: Row;
}

export default function CopyLinkMenuItem({
  setIsDropdownOpen,
  rowData,
}: CopyLinkMenuItemProps) {
  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );
  const identity = useSelector(
    (state: RootState) => state.mainReducer.identity,
  );
  const workspaceExtensionList = identity?.wsWorkspaceExtensions;

  const instagramAccountVerificationItem = workspaceExtensionList?.find(
    (extension) => extension.type === ExtensionTypeEnum.ApiConnection,
  ) as WsApiConnection | undefined;

  const { encodedData } = instagramAccountVerificationItem?.settings || {};

  const isInstagramApiPaired = () => {
    const ig = rowData.socialProfiles?.find(
      (item) => item.socialProvider === "instagram",
    );
    return ig?.flags.includes(SocialProfileFlags.ApiPaired);
  };

  const copyProfileLink = () => {
    copyLink(
      `${AppConfig.getAppRoute()}/${activeWorkspaceUuid}/creator-database?displayGlobalTask=${rowData?.uuid}`,
    );
    setIsDropdownOpen(false);
  };

  const copyVerificationUrl = () => {
    copyLink(
      `${AppConfig.getApiHost()}/fb-int/social-onboarding/workspace-onboard?data=${encodedData}`,
    );
    setIsDropdownOpen(false);
  };

  const showCopyVerificationButton = encodedData && !isInstagramApiPaired();

  return (
    <DropdownMenuHoverItem
      content={
        <>
          <DropdownMenuItem onClick={copyProfileLink}>
            <CreatorIcon />
            <IDHFormattedMessage
              id="ws_creator_profile"
              defaultMessage="Creator Profile"
            />
          </DropdownMenuItem>
          {showCopyVerificationButton && (
            <DropdownMenuItem onClick={copyVerificationUrl}>
              <TickIcon />
              <IDHFormattedMessage
                id="ws_instagram_account_integration"
                defaultMessage="Instagram Account Verification"
              />
            </DropdownMenuItem>
          )}
        </>
      }
      menuPosition="right"
    >
      <LinkIcon />
      <IDHFormattedMessage id="ws_copy_link" defaultMessage="Copy link" />
    </DropdownMenuHoverItem>
  );
}
